/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { formatPattern } from 'react-router';

import PropTypes from 'prop-types';

import useRouter from 'js/lib/useRouter';

import { AIGenerateBrandedIcon } from '@coursera/cds-icons';

import { useIsAdminCoachV2Enabled } from 'bundles/enterprise-admin-hooks/hooks/useFeatures';
import { AdminOrgSwitcherPopup } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/AdminOrgSwitcherPopup';
import { AdminProfilePill } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/AdminProfilePill';
import { SideNav } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNav';
import { SideNavItem as SideNavItemComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavItem';
import type { SideNavItem } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import { useAdminSideNavItems } from 'bundles/enterprise-admin-navigation/components/side-nav/config/useAdminSideNavItems';
import { useShouldShowAskCoursera } from 'bundles/enterprise-admin-navigation/components/side-nav/useShouldShowAskCoursera';
import { useViewAdminDashboardPageTracker } from 'bundles/enterprise-admin-navigation/components/side-nav/useViewAdminDashboardPageTracker';
import {
  ADMIN_SIDENAV_WIDTH_COLLAPSED_PX,
  ADMIN_SIDENAV_WIDTH_EXPANDED_PX,
} from 'bundles/enterprise-admin-navigation/constants';
import { useOrgSwitcherData } from 'bundles/enterprise-admin-navigation/hooks/useOrgSwitcherData';
import { ASK_COURSERA_PATH } from 'bundles/enterprise-admin-navigation/utils/pathConstants';
import withManagedOrganizations from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';
import CourseraLogoSvg from 'bundles/page/components/CourseraLogoSvg';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  logo: css`
    width: 139px;
    height: 20px;
  `,
  bottomContainer: css`
    display: flex;
    flex-direction: column;
  `,
  settingsList: css`
    list-style: none;
    padding: 0 var(--cds-spacing-200);
  `,
  profilePillAnchor: css`
    position: relative;
  `,
};

type PropsFromCaller = {
  thirdPartyOrganization: AdminDashboardOrg;
  adminRoleType?: string;
  toggleCoach: () => void;
};

type PropsFromOrganizations = {
  thirdPartyOrganizations: AdminDashboardOrg[];
};

type Props = PropsFromCaller & PropsFromOrganizations;

type Context = {
  enableSkillsPlanningForOrg: boolean;
  enableNewLearnersPage: boolean;
};

const EnterpriseAdminSideNavigation = (
  { thirdPartyOrganization, thirdPartyOrganizations, adminRoleType, toggleCoach }: Props,
  context: Context
) => {
  useViewAdminDashboardPageTracker(thirdPartyOrganization, adminRoleType);

  const [isPopupOpen, setIsPopupOpen] = React.useState(false);
  const profilePillRef = React.useRef<HTMLDivElement>(null);

  const togglePopup = React.useCallback<() => void>(() => {
    setIsPopupOpen((prev) => !prev);
  }, []);

  const closePopup = React.useCallback<() => void>(() => {
    setIsPopupOpen(false);
  }, []);

  const { sideNavTopItems, sideNavBottomItems } = useAdminSideNavItems({
    thirdPartyOrganization,
    enterpriseAdminFeatureFlags: context,
  });

  const shouldShowAskCoursera = useShouldShowAskCoursera(thirdPartyOrganization.slug);
  const isAdminCoachV2Enabled = useIsAdminCoachV2Enabled();
  const router = useRouter();

  const askCourseraHref = formatPattern(ASK_COURSERA_PATH, router.params);
  const isAskCourseraActive = router.isActive(askCourseraHref);

  // Ask Coursera lives in the component (not the declarative config) because its behavior
  // depends on the AdminCoach V2 product feature toggle: when enabled, it routes to the
  // ask-coursera page; otherwise it opens the inline coach panel. Appended after the link items.
  const topItemsToDisplay = React.useMemo<SideNavItem[]>(() => {
    if (!shouldShowAskCoursera) return sideNavTopItems;
    const askCourseraItem: SideNavItem = isAdminCoachV2Enabled
      ? {
          id: 'ASK_COURSERA',
          label: _t('Admin AI'),
          icon: AIGenerateBrandedIcon,
          href: askCourseraHref,
          isActive: isAskCourseraActive,
        }
      : {
          id: 'ASK_COURSERA',
          label: _t('Ask Coursera'),
          icon: AIGenerateBrandedIcon,
          onClick: toggleCoach,
        };
    return [...sideNavTopItems, askCourseraItem];
  }, [
    sideNavTopItems,
    shouldShowAskCoursera,
    toggleCoach,
    isAdminCoachV2Enabled,
    askCourseraHref,
    isAskCourseraActive,
  ]);

  const { contexts } = useOrgSwitcherData(thirdPartyOrganization, thirdPartyOrganizations);

  const bottomContent = React.useMemo(
    () => (
      <div css={styles.bottomContainer}>
        {sideNavBottomItems.length > 0 && (
          <ul css={styles.settingsList}>
            {sideNavBottomItems.map((item) => (
              <SideNavItemComponent key={item.id} item={item} />
            ))}
          </ul>
        )}
        <div css={styles.profilePillAnchor}>
          <div ref={profilePillRef}>
            <AdminProfilePill
              thirdPartyOrganization={thirdPartyOrganization}
              isOpen={isPopupOpen}
              onClick={togglePopup}
            />
          </div>
          <AdminOrgSwitcherPopup
            isOpen={isPopupOpen}
            onClose={closePopup}
            thirdPartyOrganization={thirdPartyOrganization}
            contexts={contexts}
            triggerRef={profilePillRef}
          />
        </div>
      </div>
    ),
    [sideNavBottomItems, thirdPartyOrganization, isPopupOpen, togglePopup, closePopup, contexts]
  );

  return (
    <SideNav
      ariaLabel={_t('Admin navigation')}
      widths={{
        expandedPx: ADMIN_SIDENAV_WIDTH_EXPANDED_PX,
        collapsedPx: ADMIN_SIDENAV_WIDTH_COLLAPSED_PX,
      }}
      logo={<CourseraLogoSvg css={styles.logo} />}
      items={topItemsToDisplay}
      bottom={bottomContent}
    />
  );
};

EnterpriseAdminSideNavigation.contextTypes = {
  enableSkillsPlanningForOrg: PropTypes.bool,
  enableNewLearnersPage: PropTypes.bool,
};

export default withManagedOrganizations<Props, PropsFromCaller>(EnterpriseAdminSideNavigation);
